import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import styled from "@mui/styles/styled";

import type { RootState } from "../store";
import Button from "../components/Button";
import logo from "../images/logo-black.png";
import googleLogo from "../images/google.png";
import colors from "../theme/colors";
import { useEffect } from "react";
import TestBotLogin from "../components/forms/TestBotLogin";

// Identity-only scopes requested at login. Feature scopes (Drive,
// Presentations) are requested incrementally on the experiment create page.
const GOOGLE_LOGIN_SCOPES = [
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile",
  "openid",
].join(" ");

const GOOGLE_CLIENT_ID = "877772936785-l3ag7htbfdirt5ojve96istg018mf96k.apps.googleusercontent.com";

export const OAUTH_STATE_KEY = "oauth_login_state";
export const OAUTH_FROM_KEY = "oauth_login_from";

const Layout = styled(Box)({
  background: colors.text.primary,
  height: "100vh",
  width: "100vw",
});

const FormContainer = styled(Paper)({
  borderRadius: 3,
  minWidth: 324,
});

const Auth = () => {
  const user = useSelector<RootState, null | User>(({ auth }) => auth.user);

  const location = useLocation();
  const { from } = (location.state as LocationState) || {
    from: { pathname: "/" },
  };

  const navigate = useNavigate();
  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [from, navigate, user]);

  const startSignIn = () => {
    const state = (crypto as any).randomUUID() as string;
    // Use localStorage (not sessionStorage) so the value survives the
    // cross-origin redirect to Google and back. Include a timestamp so the
    // callback can reject stale entries.
    localStorage.setItem(OAUTH_STATE_KEY, JSON.stringify({ state, ts: Date.now() }));
    localStorage.setItem(OAUTH_FROM_KEY, JSON.stringify(from));

    const redirectUri = `${window.location.origin}/auth/callback`;
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: GOOGLE_LOGIN_SCOPES,
      access_type: "offline",
      // Force the consent screen on every login so Google always returns a
      // refresh_token, which the backend needs for session token renewal.
      prompt: "consent",
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  return (
    <Layout display="flex" justifyContent="center" alignItems="center">
      <FormContainer>
        <Box p={8} onClick={startSignIn} textAlign="center">
          <img src={logo} style={{ maxWidth: 120 }} alt="Form Energy Logo" />
          <Box my={8}>
            <Divider />
          </Box>
          <Box my={6} display="flex" justifyContent="center">
            <img src={googleLogo} alt="Login with Google" />
            <Box ml={3}>Login with Google</Box>
          </Box>
          <Button
            color="cta"
            type="button"
            fullWidth
          >
            Continue
          </Button>
        </Box>
        {process.env.REACT_APP_ENVIRONMENT === "test" && <TestBotLogin />}
      </FormContainer>
    </Layout>
  );
};

export default Auth;
